import * as breeze from 'breeze-client';
import 'breeze-client-labs/breeze.savequeuing';
import * as moment from 'moment';
import * as _config from './config';
import * as _util from './utilities';
import * as $ from 'jquery';
import * as _bvalid from './breezeValidators';
import * as _modelBuilder from '../../typings/modelBuilder';
import { HttpClient } from '@angular/common/http';
import { resolve } from 'q';

breeze.NamingConvention.camelCase.setAsDefault();

breeze.DataType.parseDateFromServer = (source) => moment(source).toDate();

var Rights = {
    name: "rights",
    isUnmapped: true,
    nameOnServer: "Rights",
    dataType: breeze.DataType.Undefined
};

var getEntityCtor = (<any>breeze.EntityType.prototype).getEntityCtor;

// For every entity type that gets created we are extending
// the constructor to add the "rights" property
(<any>breeze.EntityType.prototype).getEntityCtor = function () {
    var origCtor = getEntityCtor.apply(this);

    var rightsProp = this.getDataProperty("rights");
    if (!rightsProp) {
        this.addProperty(new breeze.DataProperty(Rights));
        rightsProp = this.getDataProperty("rights");
    }

    if (rightsProp.isUnmapped)
        rightsProp.isUnmapped = false;


    return origCtor;
};

// Breeze does a bad thing and uses substr(-1) instead of slice(-1)
// that is browser inconsistent.  This implementation overrides the 
// default behavior
(<any>breeze.DataService)._normalizeServiceName = function (serviceName) {
    serviceName = serviceName.trim();
    if (serviceName.slice(-1) !== "/") {
        return serviceName + '/';
    } else {
        return serviceName;
    }
};


var serviceName: string = _config.serviceName;
var masterManager: breeze.EntityManager = new breeze.EntityManager(serviceName);
masterManager.saveOptions.allowConcurrentSaves = true;
masterManager.enableSaveQueuing(true);

var initialized = false;

export class EntityManagerProvider {
    private _manager: breeze.EntityManager;

    manager(): breeze.EntityManager {
        if (!this._manager) {
            this._manager = masterManager.createEmptyCopy();
            this._manager.saveOptions.allowConcurrentSaves = true;
            this._manager.enableSaveQueuing(true);

            // Populate with lookup data
            this._manager.importEntities(masterManager.exportEntities());

            // Subscribe to events
            this._manager.hasChangesChanged.subscribe(function (args) {
                //_app.trigger('hasChanges');
            });
        }

        return this._manager;
    }
}

export var modelBuilder: (metadata: breeze.MetadataStore) => void = _modelBuilder.extendMetadata;

export function create() {
    return new EntityManagerProvider();
}

var cachedMetadataHash: string;
var currentMetadataHash = _config.hashes.currentMetadataHash;

var cachedAnnotatedMetadataHash: string;
var currentAnnotatedMetadataHash = _config.hashes.currentAnnotatedMetadataHash;

var cachedLookupsHash: string;
var currentLookupsHash = _config.hashes.currentLookupsHash;

export function init(http: HttpClient): Promise<any> {
    if (initialized) {
        return _util.emptyPromise;
    }

    return http.get<any[]>(serviceName + '/init')
        .toPromise()
        .then((hashes) => {
            _config.hashes.currentMetadataHash = hashes[0].MetadataHash;
            _config.hashes.currentAnnotatedMetadataHash = hashes[0].AnnotatedMetadataHash;
            _config.hashes.currentLookupsHash = hashes[0].LookupsHash;
        })
        .then(() => {
            return prepare(http);
        })
        .then(() => {
            initialized = true;
        })
        .catch(err => {
            return Promise.reject(err);
        });
}

function prepare(_http: HttpClient) {
    cachedMetadataHash = _util.store.fetch(_config.stateKeys.metadataHash);
    cachedAnnotatedMetadataHash = _util.store.fetch(_config.stateKeys.annotatedMetadataHash);
    cachedLookupsHash = _util.store.fetch(_config.stateKeys.lookupsHash);

    return hydrateMetadata()
        .then(buildModel)
        .then(hydrateLookups)
        .then(() => {
            return hydrateAnnotatedMetadata(_http);
        });
}

function hydrateMetadata(): Promise<any> {
    var metadata = _util.store.fetch(_config.stateKeys.breezeMetadata);

    if (!cachedMetadataHash || !metadata || cachedMetadataHash !== currentMetadataHash) {
        return masterManager
            .fetchMetadata()
            .then(cacheMetadata);
    }

    masterManager.metadataStore = breeze.MetadataStore.importMetadata(metadata);
    return _util.emptyPromise;
}

function cacheMetadata() {
    var meta = masterManager.metadataStore.exportMetadata();
    _util.store.save(_config.stateKeys.breezeMetadata, meta);
    _util.store.save(_config.stateKeys.metadataHash, currentMetadataHash);
}

function buildModel() {
    modelBuilder(masterManager.metadataStore);
}

function hydrateLookups(): Promise<any> {
    var lookups = _util.store.fetch(_config.stateKeys.lookups);

    if (!cachedLookupsHash || !lookups || cachedLookupsHash !== currentLookupsHash) {

        var query = breeze.EntityQuery
            .from('lookups');

        return masterManager.executeQuery(query).then(cacheLookups);
    }

    masterManager.importEntities(lookups);
    return _util.emptyPromise;
}

function cacheLookups(ls) {
    var lookups = masterManager.exportEntities();
    _util.store.save(_config.stateKeys.lookups, lookups);
    _util.store.save(_config.stateKeys.lookupsHash, currentLookupsHash);
}

function hydrateAnnotatedMetadata(_http: HttpClient): Promise<any> {
    var annotated = _util.store.fetch(_config.stateKeys.annotatedMetadata);

    if (!cachedAnnotatedMetadataHash || !annotated || cachedAnnotatedMetadataHash !== currentAnnotatedMetadataHash) {
        return _http.get<Array<meta.AnnotatedMetadata>>(serviceName + '/annotatedmetadata')
            .toPromise()
            .then((data) => {
                parseAnnotatedMetadata(data);
                cacheAnnotatedMetadata(JSON.stringify(data));
            });
    }

    parseAnnotatedMetadata(<Array<meta.AnnotatedMetadata>>$.parseJSON(annotated));
    return _util.emptyPromise;
}

function cacheAnnotatedMetadata(data) {
    _util.store.save(_config.stateKeys.annotatedMetadata, data);
    _util.store.save(_config.stateKeys.annotatedMetadataHash, currentAnnotatedMetadataHash);
}

function parseAnnotatedMetadata(data: Array<meta.AnnotatedMetadata>) {
    var metadataStore = masterManager.metadataStore;

    data.forEach(function (metaEntity) {
        var entityType = <meta.MetaEntityType>metadataStore.getEntityType(metaEntity.key, true);

        if (entityType) {
            metadataStore[entityType.shortName] = function () {
                return entityType;
            };

            entityType.displayName = metaEntity.value.meta.displayName;
            entityType.meta = metaEntity.value.meta;
            entityType.props = {};

            var entityProps = <meta.MetaProperty[]>entityType.getProperties();
            entityProps.forEach(function (entityProp) {
                entityType.props[entityProp.name] = function () {
                    return entityProp;
                };

                var metaProp = metaEntity.value.properties[entityProp.name];

                if (metaProp) {
                    entityProp.displayName = metaProp.displayName;
                    entityProp.meta = metaProp;

                    _bvalid.setupValidators(entityProp.validators, entityProp.meta);
                }
            });
        }
    });
}
